/**
 * content.js — render helpers for the ClientWave360 landing page.
 * Ported from the redesign prototype's `app.js`. Every field is optional; sections
 * collapse when their data is empty.
 */

const DAY_ORDER = ['Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'];
const JS_DAYS = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];

/* ---------- value helpers ---------- */

export function nonEmpty(arr) {
  return Array.isArray(arr) ? arr.filter(x => x && String(x).trim()) : [];
}

/** Split free text into sentences (keeps terminal punctuation). */
export function sentences(text) {
  return (text || '').match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g) || [];
}

/**
 * The A2P/SMS consent disclosure shown under the contact form's Send button.
 * This exact text is also what the toll-free verification opt-in screenshot
 * captures, so carriers see the same language a visitor sees (REQUEST-73).
 */
export function smsConsentDisclaimer(name) {
  const by = name ? ` by ${name}` : '';
  return (
    `By providing your mobile number you grant permission to be contacted by phone or text message${by}. ` +
    'You consent to receive recurring automated and conversational messages related to our discussions, ' +
    'including personalized updates and reminders. Consent is not a condition of any purchase. ' +
    'To seek assistance and support, reply with HELP, to conclude the conversation or stop receiving messages, ' +
    'reply with STOP. Message frequency may vary. Standard message and data rates may apply. ' +
    'Review our Terms and Privacy for more information.'
  );
}

export function telHref(phone) {
  return 'tel:+1' + String(phone).replace(/\D/g, '').replace(/^1/, '');
}

export function initials(firstName, lastName) {
  return ((firstName || ' ')[0] + (lastName || ' ')[0]).trim().toUpperCase();
}

/** Strip HTML to plain text (SSR-safe — no DOMParser). */
export function stripHtml(html) {
  return (html || '')
    .toString()
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Read UTM params from the current landing URL and map them to the backend's
 * `tracking_*` contact fields (the agent share links in WebpageShareLink.jsx emit
 * utm_source / utm_medium / utm_campaign). Returns {} on the server or when absent.
 */
export function readTrackingParams() {
  if (typeof window === 'undefined') return {};
  const q = new URLSearchParams(window.location.search);
  const map = {
    utm_source: 'tracking_source',
    utm_medium: 'tracking_medium',
    utm_campaign: 'tracking_campaign'
  };
  const out = {};
  Object.keys(map).forEach(key => {
    const value = q.get(key);
    if (value && value.trim()) out[map[key]] = value.trim().slice(0, 255);
  });
  return out;
}

export function mapsUrl({ address_1, address_2, city, state, zip_code }) {
  const addr = [address_1, address_2, city, state, zip_code].filter(Boolean).join(', ');
  return addr ? 'https://maps.google.com/?q=' + encodeURIComponent(addr) : null;
}

export function mapEmbedUrl({ address_1, address_2, city, state, zip_code }) {
  const addr = [address_1, address_2, city, state, zip_code].filter(Boolean).join(', ');
  return addr ? 'https://maps.google.com/maps?q=' + encodeURIComponent(addr) + '&z=14&output=embed' : null;
}

/* ---------- office hours ---------- */

function formatTime(t) {
  const parts = String(t).split(':');
  let h = parseInt(parts[0], 10);
  const m = parts[1] || '00';
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return h + ':' + m + ' ' + suffix;
}

function dayLabel(days) {
  if (!days.length) return '';
  return days.length === 1 ? days[0] : days[0] + ' – ' + days[days.length - 1];
}

/**
 * Build ordered office-hours rows, grouping uncovered days into "Closed" runs.
 * @returns {{ id: string, days: string, time: string, isToday: boolean, isClosed: boolean }[]}
 */
export function buildHoursRows(officeHours) {
  if (!Array.isArray(officeHours) || !officeHours.length) return [];
  const rows = [];
  const covered = {};
  const todayName = JS_DAYS[new Date().getDay()];

  officeHours.forEach(g => {
    const days = (g.days || []).slice().sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
    days.forEach(d => {
      covered[d] = true;
    });
    if (!days.length || !g.timeRange) return;
    rows.push({
      id: `open-${days.join('-')}`,
      days: dayLabel(days),
      time: formatTime(g.timeRange.start) + ' – ' + formatTime(g.timeRange.end),
      isToday: days.indexOf(todayName) !== -1,
      isClosed: false
    });
  });

  let run = [];
  DAY_ORDER.concat([null]).forEach(d => {
    if (d && !covered[d]) {
      run.push(d);
      return;
    }
    if (run.length) {
      rows.push({
        id: `closed-${run.join('-')}`,
        days: dayLabel(run),
        time: 'Closed',
        isToday: run.indexOf(todayName) !== -1,
        isClosed: true
      });
      run = [];
    }
  });

  return rows;
}
